import { useEffect, useState } from "react";
import { Heart, MessageCircle, Trash2, Send, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const [showImg, setShowImg] = useState(false);
  const [busy, setBusy] = useState(false);
  const [commentFor, setCommentFor] = useState(null);
  const [commentText, setCommentText] = useState("");

  const load = () => api.get("/community").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const post = async () => {
    if (!text.trim()) { toast.error("Write something first."); return; }
    setBusy(true);
    try { await api.post("/community", { text, image_url: image }); setText(""); setImage(""); setShowImg(false); toast.success("Posted! +5 pts"); load(); }
    catch { toast.error("Could not post."); } finally { setBusy(false); }
  };

  const like = async (p) => {
    const { data } = await api.post(`/community/${p.id}/like`);
    setPosts((ps) => ps.map((x) => x.id === p.id ? { ...x, liked: data.liked, like_count: data.like_count } : x));
  };

  const comment = async (pid) => {
    if (!commentText.trim()) return;
    await api.post(`/community/${pid}/comment`, { text: commentText });
    setCommentText(""); setCommentFor(null); load();
  };

  const del = async (id) => { if (!window.confirm("Delete post?")) return; await api.delete(`/community/${id}`); load(); };

  return (
    <div data-testid="community-page" className="px-4 md:px-8 pb-16 pt-2 max-w-2xl">
      <h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight">Community</h1>
      <p className="text-muted-foreground mt-1">Share your wins, progress photos and cheer each other on.</p>

      <div className="border border-border rounded-xl bg-card p-5 mt-6" data-testid="post-composer">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="post-text" rows={3} placeholder={`What's your win today, ${(user?.name || "").split(" ")[0]}?`} className="resize-none" />
        {showImg && <Input value={image} onChange={(e) => setImage(e.target.value)} data-testid="post-image" placeholder="Image URL (optional)" className="mt-3 h-11" />}
        <div className="flex items-center justify-between mt-3">
          <button onClick={() => setShowImg((s) => !s)} className="flex items-center gap-1.5 text-xs uppercase font-bold text-muted-foreground hover:text-primary transition-colors"><ImagePlus size={16} /> Photo</button>
          <button onClick={post} disabled={busy} data-testid="post-submit" className="bg-primary text-primary-foreground px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-bold disabled:opacity-60">{busy ? "Posting…" : "Post"}</button>
        </div>
      </div>

      <div className="space-y-4 mt-6">
        {posts.length === 0 && <p className="text-muted-foreground text-center py-10">No posts yet — be the first to share!</p>}
        {posts.map((p) => (
          <div key={p.id} className="border border-border rounded-xl bg-card p-5" data-testid="community-post">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/15 text-primary grid place-items-center font-bold overflow-hidden">{p.author_pic ? <img src={p.author_pic} alt="" className="w-full h-full object-cover" /> : (p.author || "M")[0]}</div>
              <div className="flex-1 min-w-0"><div className="font-bold text-sm">{p.author}</div><div className="text-xs text-muted-foreground">{timeAgo(p.created_at)}</div></div>
              {p.user_id === user?.id && <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-primary p-1"><Trash2 size={15} /></button>}
            </div>
            <p className="mt-3 whitespace-pre-wrap">{p.text}</p>
            {p.image_url && <img src={p.image_url} alt="" className="mt-3 rounded-lg w-full object-cover max-h-96" />}
            <div className="flex items-center gap-5 mt-4 pt-3 border-t border-border">
              <button onClick={() => like(p)} data-testid="post-like" className={`flex items-center gap-1.5 text-sm font-bold transition-colors ${p.liked ? "text-primary" : "text-muted-foreground hover:text-primary"}`}><Heart size={17} className={p.liked ? "fill-primary" : ""} /> {p.like_count || 0}</button>
              <button onClick={() => setCommentFor(commentFor === p.id ? null : p.id)} data-testid="post-comment-toggle" className="flex items-center gap-1.5 text-sm font-bold text-muted-foreground hover:text-primary transition-colors"><MessageCircle size={17} /> {(p.comments || []).length}</button>
            </div>
            {(p.comments || []).length > 0 && (
              <div className="mt-3 space-y-2">
                {p.comments.map((c) => (<div key={c.id} className="text-sm"><span className="font-bold">{c.author}</span> <span className="text-muted-foreground">{c.text}</span></div>))}
              </div>
            )}
            {commentFor === p.id && (
              <div className="flex gap-2 mt-3">
                <input value={commentText} onChange={(e) => setCommentText(e.target.value)} data-testid="comment-input" placeholder="Add a comment…" className="flex-1 h-10 px-3 rounded-full border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                <button onClick={() => comment(p.id)} className="grid place-items-center w-10 h-10 rounded-full bg-primary text-primary-foreground"><Send size={15} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
